import { useCallback, useEffect, useRef, useState } from "react";
import type {
  RepairLanguage,
  RepairModule,
  RepairSearchResponse,
  Vehicle,
} from "@models/repairInformation";
import { repairInformationApi } from "@services/repairInformationApi";

export const ALL_MODULES = "all";

/** What a repair-information search is scoped to; edited in the search settings. */
export interface RepairSettings {
  vehicle: Vehicle | null;
  language: RepairLanguage;
  module: string;
  /** Minimum fused score (0–100) a hit needs to be shown. */
  threshold: number;
}

export const DEFAULT_REPAIR_SETTINGS: RepairSettings = {
  vehicle: null,
  language: "de",
  module: ALL_MODULES,
  threshold: 0,
};

/** The languages a vehicle is published in (both until a vehicle is picked). */
export function languagesFor(vehicle: Vehicle | null): RepairLanguage[] {
  return vehicle?.languages.length ? vehicle.languages : ["de", "en"];
}

/** ``settings`` with ``patch`` applied, the language kept valid for the vehicle. */
export function patchRepairSettings(settings: RepairSettings, patch: Partial<RepairSettings>): RepairSettings {
  const next = { ...settings, ...patch };
  const languages = languagesFor(next.vehicle);
  return languages.includes(next.language) ? next : { ...next, language: languages[0] };
}

export interface VehicleList {
  vehicles: Vehicle[];
  loading: boolean;
  error: string | null;
}

/** The published vehicles, loaded once, the first time ``enabled`` is true. */
export function useVehicles(enabled: boolean): VehicleList {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requested = useRef(false);

  useEffect(() => {
    if (!enabled || requested.current) return;
    requested.current = true;
    setLoading(true);
    setError(null);
    repairInformationApi
      .listVehicles()
      .then((res) => setVehicles(res.vehicles))
      .catch((err) => {
        setError(err instanceof Error ? err.message : "Could not load vehicles");
        requested.current = false;
      })
      .finally(() => setLoading(false));
  }, [enabled]);

  return { vehicles, loading, error };
}

export interface RepairSearchState {
  loading: boolean;
  error: string | null;
  result: RepairSearchResponse | null;
  run: (query: string, options: RepairSettings) => Promise<void>;
  reset: () => void;
}

/** Vehicle-scoped repair-information search. */
export function useRepairInformationSearch(): RepairSearchState {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RepairSearchResponse | null>(null);

  const run = useCallback(async (query: string, options: RepairSettings) => {
    const { vehicle } = options;
    if (!vehicle) return;
    setLoading(true);
    setError(null);
    try {
      setResult(
        await repairInformationApi.search({
          query,
          vehicle: { typeId: vehicle.typeId, bodyId: vehicle.bodyId, countryCode: vehicle.countryCode },
          language: options.language,
          module: options.module === ALL_MODULES ? null : (options.module as RepairModule),
          threshold: options.threshold,
        }),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Search failed");
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setError(null);
    setResult(null);
  }, []);

  return { loading, error, result, run, reset };
}
